import {GetEntries, Timer} from "js-vextensions";
import React, {useEffect} from "react";
import {Button, CheckBox, Column, DropDown, DropDownContent, DropDownTrigger, Row, Select, Text} from "react-vcomponents";
import {BaseComponent, UseEffect} from "react-vextensions";
import {ShowMessageBox} from "react-vmessagebox";
import {minuteInMS, Observer, RunInAction_Set} from "web-vcore";
import {store} from "../../../Store/index.js";
import {JourneySettingsTab, JourneyTab, OverlayPanel} from "../../../Store/main/tools/journey.js";
import {zIndexes} from "../../../Utils/UI/ZIndexes.js";
import {SessionLog} from "../../Tools/@Shared/BetweenSessionTypes/SessionLog.js";
import {LogType} from "../../Tools/@Shared/LogEntry.js";
import {UpdateJournalEntry} from "../../../Server/Commands/JournalEntryCommands.js";
import {LaunchType} from "../../../Server/DB/engineConfigs/@EngineConfig/@EC_ModeSwitcher.js";
import {GetLiveLocalSession_Reactive, GetLiveSession_Reactive, StartHostSession, StopHostSession} from "../../../Engine/LiveSessionManagement.js";
import {GetSelectedEngineConfig} from "../../../Server/DB/engineConfigs.js";
import {AlarmsComp} from "../../../Engine/LiveSession/Components/AlarmsComp.js";
import {JourneyViewSettingsUI} from "./DropdownPanel/JourneyViewSettingsUI.js";
import {SessionControlsUI} from "./DropdownPanel/SessionControlsUI.js";
import {observer_mgl} from "mobx-graphlink";
import {useForceUpdate} from "web-vcore";
import {LiveSession_Local} from "../../../Engine/LiveSession_Local.js";
import {SessionInfoUI} from "./DropdownPanel/SessionInfoUI.js";
import {Journey_GetSessionPeriodsToShow} from "../../../Server/DB/journalEntries.js";
import {Phase} from "../../../Server/DB/@Shared/Phase.js";

export const GetAlarmsPhaseInfo = (session: LiveSession_Local|n)=>{
	const liveAlarmsComp = session?.Comp(AlarmsComp);
	const playing = liveAlarmsComp != null;
	const result = {
		playing,
		phase: null as Phase|n,
		phase_niceName: null as string|n,
		timeOfNextPhase: null as number|n,
		timeOfNextPhase_waitingForUser: null as boolean|n,
		timeOfNextPhase_waitingForTime_asSecondsOrMinutesStr: null as string|n,
	};
	if (playing) {
		result.phase = liveAlarmsComp.phase;
		const phaseNiceNames = {
			[Phase.notStarted]: "not started",
			[Phase.initialDelay]: "initial delay",
			[Phase.tech]: "tech",
			[Phase.drifting]: "drifting",
			[Phase.dreaming]: "dreaming",
			[Phase.solving]: "solving",
			[Phase.delving]: "delving",
		} satisfies Record<Phase, string>;
		result.phase_niceName = phaseNiceNames[result.phase];

		result.timeOfNextPhase = Math.min(...[
			session?.initialDelayTimer.Enabled && session.initialDelayTimer.nextTickTime,
			liveAlarmsComp.sleepUntilAlarmTimer?.Enabled && liveAlarmsComp.sleepUntilAlarmTimer.nextTickTime,
			liveAlarmsComp.requiredSolveProgressTimer?.Enabled && liveAlarmsComp.requiredSolveProgressTimer.nextTickTime,
			//liveJourneySession.autoEndDelayTimer.Enabled && liveJourneySession.autoEndDelayTimer.nextTickTime,
		].filter(a=>a) as number[]);
		if (result.timeOfNextPhase == Infinity) {
			result.timeOfNextPhase_waitingForUser = true;
		} else {
			const minutesTillNextPhase = (result.timeOfNextPhase - Date.now()).KeepAtLeast(0) / minuteInMS;
			const secondsTillNextPhase = (result.timeOfNextPhase - Date.now()).KeepAtLeast(0) / 1000;
			result.timeOfNextPhase_waitingForTime_asSecondsOrMinutesStr = minutesTillNextPhase >= 1
				? `${Math.ceil(minutesTillNextPhase).toString()} minutes`
				: `${Math.ceil(secondsTillNextPhase).toString()} seconds`;
		}
	}
	return result;
};

export const JBanner = observer_mgl(()=>{
	const uiState = store.main.engine.journey;
	const liveSession = GetLiveSession_Reactive();
	const liveLocalSession = GetLiveLocalSession_Reactive();
	if (liveSession != null && liveLocalSession == null) {
		return <div>
			{/*<Button text="X" onClick={()=>RunInAction_Set(()=>uiState.tab = JourneyTab.config)}/>*/}
			<Text>{`A non-local FBA session is active; it must be ended (on Tools->Engine page) before using this panel.`}</Text>
		</div>
	}
	const selectedEngineConfig_tracker1 = GetSelectedEngineConfig(); // this is just here so we have synchronous access to it in startHostSession() func (and the funcs called by it)
	const liveAlarmsComp = liveLocalSession?.Comp(AlarmsComp);
	const playing = liveAlarmsComp != null;

	const {journalEntryForLiveSession} = Journey_GetSessionPeriodsToShow();

	// update banner every 1s (so "time till next phase" part is up-to-date)
	const forceUpdate = useForceUpdate();
	useEffect(()=>{
		const timer = new Timer(1000, ()=>forceUpdate()).Start();
		return ()=>timer.Stop();
	});

	const phaseInfo = GetAlarmsPhaseInfo(liveLocalSession);
	const bannerLine1 =
		!phaseInfo.playing ? "No session active." :
		`Phase: ${phaseInfo.phase_niceName!}`;
	const bannerLine2 =
		!phaseInfo.playing ? null :
		phaseInfo.timeOfNextPhase_waitingForUser ? "(waiting for user action)" :
		`(ends in: ${phaseInfo.timeOfNextPhase_waitingForTime_asSecondsOrMinutesStr})`;

	useEffect(()=>{
		// if our app was just restarted after a crash (due to the crash-checker alarms), then auto-start a journey-session after a brief delay (to ensure data required for session-start is loaded in)
		if (startURL.GetQueryVar("restoredFromCrash") == "1") {
			const timer = new Timer(30000, ()=>{
				if (g.journeySessionStartedFromCrashRecovery) return;
				g.journeySessionStartedFromCrashRecovery = true;
				SessionLog("Automatically starting journey-session after crash-recovery.", LogType.Event_Large);
				startHostSession(null);
			}, 1).Start();
			return ()=>timer.Stop();
		}
	}, []);

	const playing_regularMode = liveLocalSession?.coreData.launchType == "night";
	const playing_daytimeMode = liveLocalSession?.coreData.launchType == "day";
	const startHostSession = (launchType: LaunchType|null)=>{
		StartHostSession(launchType);
	};
	const stopSession = ()=>{
		/*liveJourneySession.Stop(false, false); // maybe temp; don't save in fba-sessions table
		SetLiveFBASession(null);*/
		const session = liveLocalSession!;
		StopHostSession(false, false);

		let saveSession = true;
		let markDreamWakeTime = journalEntryForLiveSession != null && journalEntryForLiveSession.wakeTime == null;
		const controller = ShowMessageBox({
			title: "Perform final data saves?", cancelButton: true,
			message: ()=>{
				const Change = (..._)=>controller.UpdateUI();
				return (
					<Column>
						<Row>
							<CheckBox text="Save session-info" value={saveSession} onChange={val=>Change(saveSession = val)}/>
						</Row>
						<Row>
							<CheckBox text="Mark dream-wake-time" enabled={journalEntryForLiveSession != null} value={markDreamWakeTime} onChange={val=>Change(markDreamWakeTime = val)}/>
						</Row>
					</Column>
				);
			},
			onOK: ()=>{
				if (saveSession) {
					session.FinalizeStoredData(false, true);
				}
				if (markDreamWakeTime) {
					new UpdateJournalEntry({id: journalEntryForLiveSession!.id, updates: {wakeTime: Date.now()}}).RunOnServer();
				}
			},
		});
	};

	return (
		<Row>
			{/*<Button size={32} mdIcon="close" onClick={()=>RunInAction_Set(()=>uiState.tab = JourneyTab.config)}/>*/}
			{/*<Button ml={3} size={32} mdIcon="chart-line" onClick={()=>RunInAction_Set(()=>uiState.tab = JourneyTab.stats)}/>*/}
			<Button ml={3} size={32} mdIcon="view-grid" onClick={()=>{
				RunInAction_Set(()=>uiState.overlay = OverlayPanel.recallGrid);
			}}/>
			<Button ml={3} size={32} mdIcon={playing_daytimeMode ? "pause" : "white-balance-sunny"}enabled={!playing || playing_daytimeMode}
				title="Start daytime session. (disables certain features, like alarms; affected options tinted light blue)"
				onClick={()=>{
					if (playing) {
						stopSession();
					} else {
						startHostSession("day");
					}
				}}/>
			<Button ml={3} size={32} mdIcon={playing_regularMode ? "pause" : "play"} enabled={!playing || playing_regularMode}
				title="Start night/regular session. (full functionality)"
				onClick={()=>{
					if (playing) {
						stopSession();
					} else {
						startHostSession("night");
					}
				}}/>
			<Column style={{flex: 1, alignItems: "center"}}>
				<Text style={{flex: 1}}>{bannerLine1}</Text>
				{bannerLine2 && <Text style={{flex: 1}}>{bannerLine2}</Text>}
			</Column>
			<Button ml={3} size={32} mdIcon={uiState.showAnchorEntities ? "image" : "image-frame"}
				// atm, only let this placement of the button *disable* entity-displaying (use view-panel to enable it)
				enabled={uiState.showAnchorEntities}
				onClick={()=>{
					RunInAction_Set(()=>uiState.showAnchorEntities = !uiState.showAnchorEntities);
				}}/>
			<Button ml={3} size={32} mdIcon={uiState.overlay == "none" ? "lock" : "lock-open-variant"} onClick={()=>{
				if (uiState.overlay == "none") {
					RunInAction_Set(()=>uiState.overlay = uiState.overlay_default);
					/*if (alsoHideNavigationUI) {
						document.documentElement.requestFullscreen({navigationUI: "hide"});
					}*/
				}
			}}/>
			{/*<Button ml={3} text="VTT" onClick={()=>{}}/>*/}
			<DropDown>
				<DropDownTrigger>
					<Button ml={3} mdIcon="cog" size={32}/>
				</DropDownTrigger>
				<DropDownContent style={{zIndex: zIndexes.dropDown, position: "fixed", right: 0, background: "rgba(0,0,0,.9)", borderRadius: 5}}>
					<JSessionSettingsDropDown/>
				</DropDownContent>
			</DropDown>
		</Row>
	);
});

const JSessionSettingsDropDown = observer_mgl(()=>{
	const uiState = store.main.engine.journey;
	return (
		<Column style={{minWidth: 320}}>
			<Select mb={5} displayType="button bar" options={GetEntries(JourneySettingsTab, "ui")}
				value={uiState.settingsTab} onChange={val=>RunInAction_Set(()=>uiState.settingsTab = val)}/>
			{uiState.settingsTab == JourneySettingsTab.view && <JourneyViewSettingsUI/>}
			{uiState.settingsTab == JourneySettingsTab.sessionControl && <SessionControlsUI/>}
			{uiState.settingsTab == JourneySettingsTab.sessionInfo && <SessionInfoUI/>}
		</Column>
	)
});